import { CacheEntry, CacheConfig, CACHE_RULES, CACHE_KEY_PREFIX, MAX_CACHE_ENTRIES, MAX_CACHE_SIZE_KB, CLEANUP_THRESHOLD_KB, SMART_INVALIDATION_RULES } from '@/config/cacheConfig';

export class ApiCacheService {
  private static instance: ApiCacheService;

  // Singleton pattern for consistent cache management
  public static getInstance(): ApiCacheService {
    if (!ApiCacheService.instance) {
      ApiCacheService.instance = new ApiCacheService();
      // Clear all cache on app initialization/reload
      ApiCacheService.instance.clearAllCache();
    }
    return ApiCacheService.instance;
  }

  /**
   * Get cached entry if it exists and is valid
   */
  public getCacheEntry(key: string): CacheEntry | null {
    try {
      const cachedItem = sessionStorage.getItem(key);
      if (!cachedItem) {
        return null;
      }

      const entry: CacheEntry = JSON.parse(cachedItem);
      
      if (this.isValid(entry)) {
        return entry;
      } else {
        // Remove expired entry
        this.removeCacheEntry(key);
        return null;
      }
    } catch (error) {
      console.warn('Failed to retrieve cache entry:', key, error);
      // Clean up corrupted entry
      this.removeCacheEntry(key);
      return null;
    }
  }

  /**
   * Store data in cache with TTL
   */
  public setCacheEntry(key: string, data: any, url: string, method: string, storeId: string): void {
    try {
      const ttl = this.getTTLForUrl(url);
      if (ttl === 0) {
        // URL should not be cached
        return;
      }

      const entry: CacheEntry = {
        data,
        timestamp: Date.now(),
        ttl,
        url,
        method: method.toUpperCase(),
        storeId
      };

      // Check cache size and cleanup if needed
      this.checkStorageUsage();

      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.warn('Failed to set cache entry:', key, error);
      // If storage is full, try cleaning up and retry once
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.cleanupExpiredEntries();
        try {
          sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), ttl: this.getTTLForUrl(url), url, method: method.toUpperCase(), storeId }));
        } catch (retryError) {
          console.warn('Failed to cache after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Generate cache key based on URL, method, and store ID
   */
  public generateCacheKey(url: string, method: string, storeId: string): string {
    // Create a simple hash of the URL for storage efficiency
    const urlHash = this.hashString(url);
    return `${CACHE_KEY_PREFIX}_${storeId}_${urlHash}_${method.toUpperCase()}`;
  }

  /**
   * Check if URL should be cached based on patterns
   * Only URLs explicitly listed in LONG_TERM or MEDIUM_TERM will be cached
   */
  public isCacheable(url: string, method: string): boolean {
    // Only cache GET requests
    if (method.toUpperCase() !== 'GET') {
      return false;
    }

    // Check if URL matches any cacheable patterns
    const allCacheablePatterns = [
      ...CACHE_RULES.LONG_TERM.patterns,
      ...CACHE_RULES.MEDIUM_TERM.patterns,
      ...CACHE_RULES.SEMI_DYNAMIC.patterns
    ];

    return allCacheablePatterns.some(pattern => url.startsWith(pattern));
  }

  /**
   * Check if cache entry is still valid
   */
  public isValid(entry: CacheEntry): boolean {
    if (!entry || !entry.timestamp || !entry.ttl) {
      return false;
    }

    const age = Date.now() - entry.timestamp;
    return age < entry.ttl;
  }

  /**
   * Clear all cache for a specific store
   */
  public clearStoreCache(storeId: string): void {
    try {
      const keysToRemove: string[] = [];
      
      // Find all keys for this store
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(`${CACHE_KEY_PREFIX}_${storeId}_`)) {
          keysToRemove.push(key);
        }
      }

      // Remove all store-specific cache entries
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      console.log(`Cleared ${keysToRemove.length} cache entries for store ${storeId}`);
    } catch (error) {
      console.warn('Failed to clear store cache:', error);
    }
  }

  /**
   * Clear all cache entries
   */
  public clearAllCache(): void {
    try {
      const keysToRemove: string[] = [];
      
      // Find all cache keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      // Remove all cache entries
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      console.log(`Cleared ${keysToRemove.length} total cache entries`);
    } catch (error) {
      console.warn('Failed to clear all cache:', error);
    }
  }

  /**
   * Smart invalidation - clear cache based on URL patterns
   */
  public clearCacheByPatterns(patterns: string[], storeId: string): void {
    try {
      const keysToRemove: string[] = [];
      
      // Find cache keys that match any of the patterns
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(`${CACHE_KEY_PREFIX}_${storeId}_`)) {
          const item = sessionStorage.getItem(key);
          if (item) {
            try {
              const entry: CacheEntry = JSON.parse(item);
              // Check if this entry's URL matches any of the patterns
              if (patterns.some(pattern => entry.url.includes(pattern))) {
                keysToRemove.push(key);
              }
            } catch {
              // Corrupted entry, remove it
              keysToRemove.push(key);
            }
          }
        }
      }

      // Remove matched cache entries
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`Smart invalidation: Cleared ${keysToRemove.length} cache entries for patterns:`, patterns);
      }
    } catch (error) {
      console.warn('Failed to clear cache by patterns:', error);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  public getCacheStats(): { totalEntries: number; validEntries: number; expiredEntries: number; totalSizeKB: number } {
    let totalEntries = 0;
    let validEntries = 0;
    let expiredEntries = 0;
    let totalSize = 0;

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          totalEntries++;
          const item = sessionStorage.getItem(key);
          if (item) {
            totalSize += item.length;
            try {
              const entry: CacheEntry = JSON.parse(item);
              if (this.isValid(entry)) {
                validEntries++;
              } else {
                expiredEntries++;
              }
            } catch {
              expiredEntries++;
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
    }

    return {
      totalEntries,
      validEntries,
      expiredEntries,
      totalSizeKB: Math.round(totalSize / 1024)
    };
  }

  // Private helper methods

  /**
   * Get TTL for a specific URL based on cache rules
   */
  private getTTLForUrl(url: string): number {
    // Check long-term cache patterns
    for (const pattern of CACHE_RULES.LONG_TERM.patterns) {
      if (url.startsWith(pattern)) {
        return CACHE_RULES.LONG_TERM.ttl;
      }
    }

    // Check medium-term cache patterns
    for (const pattern of CACHE_RULES.MEDIUM_TERM.patterns) {
      if (url.startsWith(pattern)) {
        return CACHE_RULES.MEDIUM_TERM.ttl;
      }
    }

    // Check semi-dynamic cache patterns
    for (const pattern of CACHE_RULES.SEMI_DYNAMIC.patterns) {
      if (url.startsWith(pattern)) {
        return CACHE_RULES.SEMI_DYNAMIC.ttl;
      }
    }

    // Default: no caching
    return 0;
  }

  /**
   * Simple string hashing for cache keys
   */
  private hashString(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(16);
  }

  /**
   * Remove a specific cache entry
   */
  private removeCacheEntry(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove cache entry:', key, error);
    }
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredEntries(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          const item = sessionStorage.getItem(key);
          if (item) {
            try {
              const entry: CacheEntry = JSON.parse(item);
              if (!this.isValid(entry)) {
                keysToRemove.push(key);
              }
            } catch {
              // Corrupted entry, remove it
              keysToRemove.push(key);
            }
          }
        }
      }

      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`Cleaned up ${keysToRemove.length} expired cache entries`);
      }
    } catch (error) {
      console.warn('Failed to cleanup expired entries:', error);
    }
  }

  /**
   * Proactively check storage usage and cleanup if needed
   */
  private checkStorageUsage(): void {
    try {
      const stats = this.getCacheStats();
      
      // If approaching size limit, cleanup proactively
      if (stats.totalSizeKB >= CLEANUP_THRESHOLD_KB) {
        console.warn(`Cache approaching size limit (${stats.totalSizeKB}KB/${MAX_CACHE_SIZE_KB}KB). Cleaning up...`);
        this.cleanupOldestEntries(0.3); // Remove 30% of oldest entries
      }
      
      // If exceeded max entries, cleanup
      if (stats.totalEntries >= MAX_CACHE_ENTRIES) {
        console.warn(`Cache exceeded max entries (${stats.totalEntries}/${MAX_CACHE_ENTRIES}). Cleaning up...`);
        this.cleanupOldestEntries(0.2); // Remove 20% of oldest entries
      }
    } catch (error) {
      console.warn('Failed to check storage usage:', error);
    }
  }

  /**
   * Clean up oldest cache entries by percentage
   */
  private cleanupOldestEntries(percentage: number = 0.2): void {
    try {
      const cacheEntries: Array<{ key: string; timestamp: number; size: number }> = [];
      
      // Collect all cache entries with metadata
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          const item = sessionStorage.getItem(key);
          if (item) {
            try {
              const entry: CacheEntry = JSON.parse(item);
              cacheEntries.push({ 
                key, 
                timestamp: entry.timestamp || 0,
                size: item.length
              });
            } catch {
              // Corrupted entry, mark for removal
              cacheEntries.push({ key, timestamp: 0, size: item.length });
            }
          }
        }
      }

      if (cacheEntries.length === 0) return;

      // Sort by timestamp (oldest first)
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Calculate how many entries to remove
      const entriesToRemove = Math.ceil(cacheEntries.length * percentage);
      const oldestEntries = cacheEntries.slice(0, entriesToRemove);
      
      // Remove oldest entries
      let removedSize = 0;
      oldestEntries.forEach(entry => {
        sessionStorage.removeItem(entry.key);
        removedSize += entry.size;
      });
      
      const removedSizeKB = Math.round(removedSize / 1024);
      console.log(`Cleaned up ${oldestEntries.length} old cache entries, freed ${removedSizeKB}KB`);
      
    } catch (error) {
      console.warn('Failed to cleanup oldest entries:', error);
    }
  }

  /**
   * Enforce storage limit by removing oldest entries if needed (legacy method)
   */
  private enforceStorageLimit(): void {
    try {
      const cacheEntries: Array<{ key: string; timestamp: number }> = [];
      
      // Collect all cache entries with timestamps
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          const item = sessionStorage.getItem(key);
          if (item) {
            try {
              const entry: CacheEntry = JSON.parse(item);
              cacheEntries.push({ key, timestamp: entry.timestamp || 0 });
            } catch {
              // Corrupted entry, will be removed
              cacheEntries.push({ key, timestamp: 0 });
            }
          }
        }
      }

      // If we exceed the limit, remove oldest entries
      if (cacheEntries.length >= MAX_CACHE_ENTRIES) {
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
        const entriesToRemove = cacheEntries.slice(0, cacheEntries.length - MAX_CACHE_ENTRIES + 100); // Remove extra entries
        
        entriesToRemove.forEach(entry => sessionStorage.removeItem(entry.key));
        
        console.log(`Removed ${entriesToRemove.length} old cache entries to enforce limit`);
      }
    } catch (error) {
      console.warn('Failed to enforce storage limit:', error);
    }
  }
}

// Export singleton instance
export const apiCacheService = ApiCacheService.getInstance();